'use client';

import type { QueryKey } from '@morfi/backend-sdk-v2/client';
import { type Context, createContext, use } from 'react';
import { createStore, type StoreApi, useStore } from 'zustand';
import type { MarketChartOpening } from '@/lib/chart/market-chart-opening';
import type { ChartInterval, ChartMode, YAxisProperty } from '@/lib/chart/types';
import { useChartSettingsStore } from './chart-settings';

interface ChartIntentSettings {
  readonly key: string;
  readonly chartMode: ChartMode;
  readonly yAxisProperty: YAxisProperty;
  readonly interval: ChartInterval;
  readonly renderMode: 'line' | 'candle';
  readonly startedAt: number;
}

export type ChartIntentSelection = ChartIntentSettings &
  (
    | { readonly kind: 'market'; readonly marketPubkey: string }
    | { readonly kind: 'winner'; readonly outcomeMint: string }
  );

export type MarketChartIntentSelection = Extract<ChartIntentSelection, { kind: 'market' }>;
export type WinnerChartIntentSelection = Extract<ChartIntentSelection, { kind: 'winner' }>;

interface ChartIntentState {
  readonly selection: ChartIntentSelection | null;
  readonly opening: MarketChartOpening | undefined;
  warm: (href: string) => void;
  clear: (key: string) => void;
  registerConviction: (
    selection: MarketChartIntentSelection,
    interval: ChartInterval,
    anchorMinuteMs: number,
    readSnapshot: NonNullable<MarketChartOpening['readConvictionSnapshot']>,
  ) => void;
  register: (
    selection: MarketChartIntentSelection,
    interval: ChartInterval,
    outcomeIndex: number,
    queryKey: QueryKey,
    chartType: 'line' | 'ohlcv',
    section?: 'main' | 'terminal',
  ) => void;
}

/** Only UI intent and SDK-owned query identities live here; chart data stays in the SDK. */
export function createChartIntentStore(): StoreApi<ChartIntentState> {
  return createStore<ChartIntentState>((set, get) => ({
    selection: null,
    opening: undefined,
    warm: (href: string) => {
      const match = /(?:^|\/)(markets|winners)\/([1-9A-HJ-NP-Za-km-z]{32,44})(?:[?#]|$)/.exec(href);
      const identifier = match?.[2];
      if (identifier === undefined || !href.startsWith('/')) return;
      const target =
        match?.[1] === 'winners'
          ? { kind: 'winner' as const, outcomeMint: identifier }
          : { kind: 'market' as const, marketPubkey: identifier };
      const { chartMode, yAxisProperty, interval, renderMode } = useChartSettingsStore.getState();
      const mode = target.kind === 'winner' ? 'live' : chartMode;
      const key = [target.kind, identifier, mode, yAxisProperty, interval, renderMode].join('|');
      if (get().selection?.key === key) return;
      set({
        selection: {
          key,
          ...target,
          chartMode: mode,
          yAxisProperty,
          interval,
          renderMode,
          startedAt: Date.now(),
        },
        opening: undefined,
      });
    },
    clear: (key: string) => {
      if (get().selection?.key === key) set({ selection: null, opening: undefined });
    },
    registerConviction: (
      selection: MarketChartIntentSelection,
      interval: ChartInterval,
      anchorMinuteMs: number,
      readSnapshot: NonNullable<MarketChartOpening['readConvictionSnapshot']>,
    ) => {
      if (get().selection !== selection) return;
      const previous = get().opening;
      if (
        previous?.property === 'twar' &&
        previous.interval === interval &&
        previous.anchorMinuteMs === anchorMinuteMs &&
        previous.readConvictionSnapshot === readSnapshot
      ) {
        return;
      }
      set({
        opening: {
          property: 'twar',
          marketPubkey: selection.marketPubkey,
          interval,
          anchorMinuteMs,
          allSnapshotToMs: anchorMinuteMs,
          readConvictionSnapshot: readSnapshot,
          queries: [],
        },
      });
    },
    register: (
      selection: MarketChartIntentSelection,
      interval: ChartInterval,
      outcomeIndex: number,
      queryKey: QueryKey,
      chartType: 'line' | 'ohlcv',
      section: 'main' | 'terminal' = 'main',
    ) => {
      if (get().selection !== selection) return;
      const previous = get().opening;
      const queries =
        (section === 'terminal' ? previous?.terminalQueries : previous?.queries) ?? [];
      const existing = queries.find((query) => query.outcomeIndex === outcomeIndex);
      if (JSON.stringify(existing?.queryKey) === JSON.stringify(queryKey)) return;
      set({
        opening: {
          marketPubkey: selection.marketPubkey,
          property: selection.yAxisProperty === 'price' ? 'price' : 'market_cap',
          interval,
          chartType: section === 'main' ? chartType : (previous?.chartType ?? 'line'),
          anchorMinuteMs: Math.floor(selection.startedAt / 60_000) * 60_000,
          allSnapshotToMs: selection.startedAt,
          queries: previous?.queries ?? [],
          terminalQueries: previous?.terminalQueries ?? [],
          [section === 'terminal' ? 'terminalQueries' : 'queries']: [
            ...queries.filter((query) => query.outcomeIndex !== outcomeIndex),
            { outcomeIndex, queryKey },
          ],
        },
      });
    },
  }));
}

export type ChartIntentStore = ReturnType<typeof createChartIntentStore>;
export const ChartIntentContext: Context<ChartIntentStore | null> =
  createContext<ChartIntentStore | null>(null);

export function useChartIntentStore(): ChartIntentStore {
  const store = use(ChartIntentContext);
  if (store === null) throw new Error('Chart intent requires ChartIntentProvider');
  return store;
}

export function useChartIntentOpening(marketPubkey: string): MarketChartOpening | undefined {
  const store = useChartIntentStore();
  const interval = useChartSettingsStore((state) => state.interval);
  const chartMode = useChartSettingsStore((state) => state.chartMode);
  const yAxisProperty = useChartSettingsStore((state) => state.yAxisProperty);
  return useStore(store, (state) =>
    state.selection?.kind === 'market' &&
    state.selection.marketPubkey === marketPubkey &&
    state.selection.interval === interval &&
    state.selection.chartMode === chartMode &&
    state.selection.yAxisProperty === yAxisProperty
      ? state.opening
      : undefined,
  );
}

export function useWinnerChartIntentAnchor(outcomeMint: string): number | undefined {
  const store = useChartIntentStore();
  const interval = useChartSettingsStore((state) => state.interval);
  const property = useChartSettingsStore((state) => state.yAxisProperty);
  const renderMode = useChartSettingsStore((state) => state.renderMode);
  return useStore(store, (state) =>
    state.selection?.kind === 'winner' &&
    state.selection.outcomeMint === outcomeMint &&
    state.selection.interval === interval &&
    state.selection.yAxisProperty === property &&
    state.selection.renderMode === renderMode
      ? Math.floor(state.selection.startedAt / 60_000) * 60_000
      : undefined,
  );
}

export function useChartIntentSelection(store: ChartIntentStore): ChartIntentSelection | null {
  return useStore(store, (state) => state.selection);
}
